import React, { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { CanDisable } from '@modernloop/shared/helper-components';
import { TriangleDownIcon } from '@modernloop/shared/icons';
import { EmployeeId } from '@modernloop/shared/ids';
import { Button, Typography } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  EmployeeFragment,
  InterviewMeetingLocationSettingPref,
  VideoMeetingHost_InterviewMeetingLocationSettingPrefFragment,
} from 'src/generated/mloop-graphql';

import SingleEmployeeSelect from 'src/entities/Select/EmployeeSelect/SingleEmployeeSelect';
import { SmartOptions } from 'src/entities/Select/EmployeeSelect/types';

import { InterviewMeetingLocationVariant } from '../types';

import VideoMeetingHostDynamicHost from './VideoMeetingHostDynamicHost';
import VideoMeetingHostEmployeeValue from './VideoMeetingHostEmployeeValue';

type Fragments = {
  interviewMeetingLocationSettingPref: VideoMeetingHost_InterviewMeetingLocationSettingPrefFragment;
};

type Props = {
  disabled: boolean;
  variant?: InterviewMeetingLocationVariant;
  onChange: (pref: InterviewMeetingLocationSettingPref) => void;
};

const VideoMeetingHost: FCWithFragments<Fragments, Props> = ({
  disabled,
  variant = InterviewMeetingLocationVariant.INTERVIEW,
  interviewMeetingLocationSettingPref,
  onChange,
}) => {
  const { t } = useTranslation();
  const { dynamicHost } = interviewMeetingLocationSettingPref;
  const hostEmployeeId = EmployeeId.parseOrNull(interviewMeetingLocationSettingPref.hostEmployeeId);

  const getLabel = useCallback(() => {
    let content: React.ReactNode;
    if (hostEmployeeId) {
      content = <VideoMeetingHostEmployeeValue employeeId={hostEmployeeId} />;
    } else if (dynamicHost) {
      content = <VideoMeetingHostDynamicHost dynamicHost={dynamicHost} />;
    } else {
      content = <Typography noWrap>{t('hiring-dashboard-common:common.unknown')}</Typography>;
    }
    return (
      <Button fullWidth endIcon={<TriangleDownIcon />} sx={{ borderRadius: 1.5, justifyContent: 'space-between' }}>
        {content}
      </Button>
    );
  }, [hostEmployeeId, dynamicHost, t]);

  const handleOnEmloyeeSelect = useCallback(
    (emp: EmployeeFragment) => {
      onChange({
        interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
        dynamicHost: undefined,
        hostEmployeeId: EmployeeId.parse(emp.id),
        // Preserve the chosen link scope across a host change.
        videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
      });
    },
    [
      interviewMeetingLocationSettingPref.interviewMeetingLocationType,
      interviewMeetingLocationSettingPref.videoMeetingLinkScope,
      onChange,
    ]
  );

  const handleOnSmartOptionSelected = useCallback(
    (smartOption: SmartOptions) => {
      onChange({
        interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
        dynamicHost:
          smartOption === SmartOptions.Interviewer
            ? DynamicInterviewMeetingHost.Interviewers
            : DynamicInterviewMeetingHost.Scheduler,
        hostEmployeeId: undefined,
        // Preserve the chosen link scope across a host change.
        videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
      });
    },
    [
      interviewMeetingLocationSettingPref.interviewMeetingLocationType,
      interviewMeetingLocationSettingPref.videoMeetingLinkScope,
      onChange,
    ]
  );

  return (
    <CanDisable disabled={disabled}>
      <SingleEmployeeSelect
        showSmartOptions
        // A debrief is one meeting with one host, so "Interviewers" is not offered (the backend
        // validator rejects the INTERVIEWERS dynamic host for debrief prefs).
        hideInterviewersSmartOption={variant === InterviewMeetingLocationVariant.DEBRIEF}
        getLabel={getLabel}
        onEmployeeSelect={handleOnEmloyeeSelect}
        onSmartOptionSelected={handleOnSmartOptionSelected}
      />
    </CanDisable>
  );
};

VideoMeetingHost.fragments = {
  interviewMeetingLocationSettingPref: gql`
    fragment VideoMeetingHost_interviewMeetingLocationSettingPref on InterviewMeetingLocationSettingPref {
      interviewMeetingLocationType
      videoMeetingLinkScope
      dynamicHost
      hostEmployeeId
    }
  `,
};

export default VideoMeetingHost;
